import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Gift, Copy, CheckCircle, Share2, ChevronDown, ChevronUp, 
  ExternalLink, Users, QrCode, X, Mail, Sparkles, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import confetti from 'canvas-confetti';
import ReferralLeaderboard from './ReferralLeaderboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social platform icons (inline SVGs for reliability)
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
  </svg>
);

/**
 * ReferralWidget - Full-featured user referral component
 * Supports promotional mode (with prizes) and general mode (organic sharing)
 */
const ReferralWidget = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const celebratedRef = useRef(false);

  const fetchReferralInfo = useCallback(async () => {
    if (!userId) return;
    
    try {
      // Get session token from localStorage
      const sessionToken = localStorage.getItem('sessionToken');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${API_URL}/api/user/referral`, { headers });
      const data = await response.json();
      if (data.success !== false && !data.detail) {
        setReferralData({ success: true, ...data });
        
        // Trigger confetti if user just qualified
        if (data.progress?.qualified && !celebratedRef.current) {
          celebratedRef.current = true;
          triggerConfetti();
        }
      }
    } catch (error) {
      console.error('Error fetching referral info:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchReferralInfo();
  }, [fetchReferralInfo]);

  const triggerConfetti = () => {
    const count = 200;
    const defaults = { origin: { y: 0.7 } };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio)
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55, colors: ['#df1283', '#FF7901', '#0FFFE2'] });
    fire(0.2, { spread: 60, colors: ['#df1283', '#FF7901'] });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8, colors: ['#0FFFE2', '#df1283'] });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2, colors: ['#FF7901'] });
    fire(0.1, { spread: 120, startVelocity: 45, colors: ['#0FFFE2'] });
  };

  const handleCopy = async () => {
    if (!referralData?.referral_link) return;
    
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const shareOnPlatform = (platform) => {
    if (!referralData) return;
    
    const link = referralData.referral_link;
    const title = referralData.share_title || 'Join me on UnCloned Journal!';
    const description = referralData.share_description || 'Start your daily journaling journey.';
    const customMessage = referralData.campaign?.custom_share_message || '';
    const text = customMessage || `${title} ${description}`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(link)}&description=${encodeURIComponent(text)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${link}`)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Don't render if referral system is disabled
  if (!loading && (!referralData || !referralData.enabled)) {
    return null;
  }

  if (loading) {
    return (
      <div className="glass-card p-4 mb-6 animate-pulse">
        <div className="h-12 bg-white/10 rounded-lg"></div>
      </div>
    );
  }

  const isPromotional = referralData.mode === 'promotional' && referralData.campaign;
  const campaign = referralData.campaign;
  const progress = referralData.progress || { count: 0, target: 3 };

  return (
    <>
      <div 
        className="glass-card overflow-hidden mb-6"
        style={{
          background: isPromotional 
            ? 'linear-gradient(135deg, rgba(223, 18, 131, 0.15) 0%, rgba(255, 119, 1, 0.1) 100%)'
            : 'rgba(255,255,255,0.03)',
          border: isPromotional 
            ? '1px solid rgba(223, 18, 131, 0.3)'
            : '1px solid rgba(255,255,255,0.1)'
        }}
        data-testid="referral-widget"
      >
        {/* Header - Always visible */}
        <div 
          className="p-4 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #df1283 0%, #FF7901 100%)' }}
              >
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">
                  {isPromotional ? 'Refer & Win' : 'Share with Friends'}
                </h3>
                <p className="text-xs text-white/60">
                  {isPromotional ? campaign?.name : 'Invite friends to journal'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isPromotional && progress.qualified && (
                <span className="px-2 py-0.5 bg-[#0FFFE2]/20 text-[#0FFFE2] text-xs rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Qualified!
                </span>
              )}
              {expanded ? (
                <ChevronUp className="w-5 h-5 text-white/50" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white/50" />
              )}
            </div>
          </div>
          
          {/* Progress Bar - Only for promotional mode */}
          {isPromotional && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-white/70">Your progress</span>
                <span className="text-[#0FFFE2] font-semibold">{progress.count} of {progress.target}</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min(100, (progress.count / progress.target) * 100)}%`,
                    background: progress.qualified 
                      ? '#0FFFE2'
                      : 'linear-gradient(90deg, #df1283 0%, #FF7901 100%)'
                  }}
                />
              </div>
              {!progress.qualified && progress.target - progress.count > 0 && (
                <p className="text-xs text-white/50 mt-1">
                  {progress.target - progress.count} more to qualify
                </p>
              )}
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4">
            {/* Prize Info - Promotional only */}
            {isPromotional && campaign && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                {campaign.prize_image_url ? (
                  <img 
                    src={campaign.prize_image_url} 
                    alt={campaign.prize_name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="text-3xl">🏆</div>
                )}
                <div>
                  <p className="text-white font-medium text-sm">Win: {campaign.prize_name}</p>
                  {campaign.end_date && (
                    <p className="text-white/50 text-xs">
                      Ends: {new Date(campaign.end_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Referral Link */}
            <div>
              <label className="text-white/60 text-xs mb-2 block">Your unique link</label>
              <div className="flex gap-2">
                <div 
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-mono truncate flex items-center"
                  style={{ 
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'white'
                  }}
                >
                  <span className="truncate">{referralData.referral_link}</span>
                </div>
                <Button
                  onClick={handleCopy}
                  size="sm"
                  className={`px-3 ${copied ? 'bg-[#0FFFE2] text-black' : 'bg-[#df1283] hover:bg-[#F02D9A]'}`}
                  data-testid="copy-referral-link"
                >
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={() => setShowQR(true)}
                  size="sm"
                  variant="outline"
                  className="px-3 border-white/20 text-white hover:bg-white/10"
                  data-testid="show-qr-code"
                >
                  <QrCode className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Social Sharing */}
            <div>
              <label className="text-white/60 text-xs mb-2 block">Share on</label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => shareOnPlatform('twitter')}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 gap-2"
                >
                  <TwitterIcon /> X
                </Button>
                <Button
                  onClick={() => shareOnPlatform('facebook')}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 gap-2"
                >
                  <FacebookIcon /> Facebook
                </Button>
                <Button
                  onClick={() => shareOnPlatform('linkedin')}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 gap-2"
                >
                  <LinkedInIcon /> LinkedIn
                </Button>
                <Button
                  onClick={() => shareOnPlatform('pinterest')}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 gap-2"
                >
                  <PinterestIcon /> Pinterest
                </Button>
                <Button
                  onClick={() => shareOnPlatform('email')}
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 gap-2"
                >
                  <Mail className="w-4 h-4" /> Email
                </Button>
              </div>
            </div>

            {/* My Referrals List */}
            {referralData.referrals && referralData.referrals.length > 0 && (
              <div>
                <label className="text-white/60 text-xs mb-2 block flex items-center gap-1">
                  <Users className="w-3 h-3" /> Your Referrals ({referralData.referrals.length})
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {referralData.referrals.map((ref, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 rounded-lg bg-white/5"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-xs font-bold">
                          {ref.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span className="text-white text-sm">{ref.name || 'Friend'}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        ref.status === 'active' 
                          ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]'
                          : 'bg-white/10 text-white/50'
                      }`}>
                        {ref.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats for General Mode */}
            {!isPromotional && (
              <div className="flex gap-4 p-3 rounded-lg bg-white/5">
                <div className="text-center flex-1">
                  <p className="text-xl font-bold text-[#0FFFE2]">{referralData.total_referrals || 0}</p>
                  <p className="text-xs text-white/50">Friends Referred</p>
                </div>
                <div className="w-px bg-white/10"></div>
                <div className="text-center flex-1">
                  <p className="text-xl font-bold text-[#FF7901]">
                    {referralData.referrals?.filter(r => r.status === 'active').length || 0}
                  </p>
                  <p className="text-xs text-white/50">Now Active</p>
                </div>
              </div>
            )}

            {/* Campaign Rules - Promotional only */}
            {isPromotional && campaign?.rules && campaign.rules.length > 0 && (
              <div className="p-3 rounded-lg bg-white/5">
                <p className="text-xs text-white/60 mb-2">How to qualify:</p>
                <ul className="space-y-1">
                  {campaign.rules.map((rule, idx) => (
                    <li key={idx} className="text-xs text-white/80 flex items-start gap-2">
                      <CheckCircle className="w-3 h-3 text-[#0FFFE2] mt-0.5 flex-shrink-0" />
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Leaderboard Toggle */}
            <Button
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              variant="outline"
              size="sm"
              className="w-full border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 gap-2"
              data-testid="toggle-leaderboard"
            >
              <Trophy className="w-4 h-4" />
              {showLeaderboard ? 'Hide Leaderboard' : 'View Top Referrers'}
            </Button>

            {/* Leaderboard Section */}
            {showLeaderboard && (
              <div className="mt-2">
                <ReferralLeaderboard userId={userId} compact={true} limit={5} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQR(false)}
        >
          <div 
            className="bg-[#1a0527] p-6 rounded-xl border border-[#df1283]/30 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-medium">Your Referral QR Code</h3>
              <button onClick={() => setShowQR(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <QRCode 
                value={referralData.referral_link} 
                size={200}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
            <p className="text-center text-white/60 text-sm mt-4">
              Scan to join via your referral link
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ReferralWidget;
